import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiRefreshCw,
  FiDollarSign,
  FiRotateCcw,
  FiClock,
} from "react-icons/fi";

// Import constants
import { PROPERTY_FORM_CONFIG } from "../../constants/aiPriceData";

// Import utils
import { getOptimizedToastContainerProps } from "../../utils/toastUtils";
import { validateFormData, exportFormData } from "./utils/aiPriceUtils";
import { USAGE_CONFIG } from "../../config/usageConfig";

// Import components
import {
  PropertyTypeSelector,
  PropertyDetailsForm,
  QuotaInfo,
} from "./components";
import UsageLimitModal from "../UsageLimitModal";

// Import hooks
import {
  useFormData,
  useManusAnalysis,
  useDropdownState,
  useTestMode,
} from "./hooks";

const AIPrice = () => {
  const navigate = useNavigate();

  // Custom hooks
  const {
    formData,
    handleFormDataChange,
    handlePropertyTypeChange,
    handleResetForm,
    loadSavedData,
  } = useFormData();

  const {
    isAnalyzing,
    analysisProgress,
    manusResponse,
    uploadedFiles,
    handleAnalyzePrice,
    handleAnalyzePriceFirebase, // Firebase version mới
    resetAnalysis,
    cleanupWebSocket,
    fetchAndProcessManusFiles,

    // Firebase Realtime tracking states
    isFirebaseTracking,
    firebaseProgress,
    firebaseStatus,
    firebaseFiles,
    firebaseError,
    isCompleted,
    isFailed,
    currentRequestId,

    // Firebase tracking controls
    // startTracking, // Temporarily unused
    // stopTracking, // Temporarily unused
    resetTracking,
  } = useManusAnalysis(formData);

  const {
    isTestMode,
    // toggleTestMode, // Temporarily unused
    isAnalyzing: isTestAnalyzing,
    analysisProgress: testAnalysisProgress,
    manusResponse: testManusResponse,
    uploadedFiles: testUploadedFiles,
    handleAnalyzePrice: handleTestAnalyzePrice,
    resetAnalysis: resetTestAnalysis,
    cleanupWebSocket: cleanupTestWebSocket,
  } = useTestMode(formData);

  const {
    activeDropdown,
    toggleDropdown,
    closeDropdown,
    createRefCallback,
    propertyTypeDropdownRef,
  } = useDropdownState();

  // State for tracking method
  const [useFirebaseTracking] = useState(false);

  // State for accordion sections
  const [expandedSections, setExpandedSections] = useState({
    propertySelection: true,
    propertyDetails: true,
  });

  // State for daily usage limit modal
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState({ used: 0, limit: USAGE_CONFIG.DEFAULT_DAILY_LIMIT });

  // State for submit tracking
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoized styles
  const transitionStyle = useMemo(
    () => ({
      transform: "none",
      willChange: "transform, opacity",
      backfaceVisibility: "hidden",
    }),
    []
  );

  // Get current property config
  const currentConfig = useMemo(() => {
    return formData.propertyType
      ? PROPERTY_FORM_CONFIG[formData.propertyType]
      : null;
  }, [formData.propertyType]);

  // Validate form
  const formValidation = useMemo(() => {
    return validateFormData(formData, currentConfig);
  }, [formData, currentConfig]);

  // Toggle section
  const toggleSection = useCallback((section, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  // Handle property type change with additional logic
  const handlePropertyTypeChangeWrapper = useCallback(
    (propertyType) => {
      handlePropertyTypeChange(propertyType);
      closeDropdown();

      // Reset analysis
      resetAnalysis();
      resetTracking(); // Reset Firebase tracking

      // Expand property details section
      setExpandedSections((prev) => ({
        ...prev,
        propertyDetails: true,
      }));
    },
    [handlePropertyTypeChange, closeDropdown, resetAnalysis, resetTracking]
  );

  // Get current analysis state based on test mode and tracking method
  const currentAnalysisState = useMemo(() => {
    if (isTestMode) {
      return {
        isAnalyzing: isTestAnalyzing,
        analysisProgress: testAnalysisProgress,
        manusResponse: testManusResponse,
        uploadedFiles: testUploadedFiles,
        handleAnalyzePrice: handleTestAnalyzePrice,
        resetAnalysis: resetTestAnalysis,
        cleanupWebSocket: cleanupTestWebSocket,
        // Test mode không support Firebase tracking yet
        isFirebaseMode: false,
      };
    }

    // Production mode với Firebase hoặc Polling
    const baseState = {
      manusResponse,
      resetAnalysis,
      cleanupWebSocket,
      fetchAndProcessManusFiles,
    };

    if (useFirebaseTracking) {
      return {
        ...baseState,
        isAnalyzing: isFirebaseTracking,
        analysisProgress: firebaseProgress || analysisProgress,
        uploadedFiles: firebaseFiles || uploadedFiles,
        handleAnalyzePrice: handleAnalyzePriceFirebase,
        resetAnalysis: () => {
          resetAnalysis();
          resetTracking();
        },
        isFirebaseMode: true,
        firebaseStatus,
        firebaseError,
        isCompleted,
        isFailed,
        currentRequestId,
      };
    }

    return {
      ...baseState,
      isAnalyzing,
      analysisProgress,
      uploadedFiles,
      handleAnalyzePrice,
      isFirebaseMode: false,
    };
  }, [
    isTestMode,
    useFirebaseTracking,
    isTestAnalyzing,
    testAnalysisProgress,
    testManusResponse,
    testUploadedFiles,
    handleTestAnalyzePrice,
    resetTestAnalysis,
    cleanupTestWebSocket,
    isAnalyzing,
    analysisProgress,
    manusResponse,
    uploadedFiles,
    handleAnalyzePrice,
    handleAnalyzePriceFirebase,
    resetAnalysis,
    cleanupWebSocket,
    fetchAndProcessManusFiles,
    isFirebaseTracking,
    firebaseProgress,
    firebaseFiles,
    firebaseStatus,
    firebaseError,
    isCompleted,
    isFailed,
    currentRequestId,
    resetTracking,
  ]);

  // Handle reset form with additional logic
  const handleResetFormWrapper = useCallback(() => {
    handleResetForm();
    currentAnalysisState.resetAnalysis();
    closeDropdown();

    // Reset Firebase tracking if in Firebase mode
    if (useFirebaseTracking) {
      resetTracking();
    }

    // Reset sections
    setExpandedSections({
      propertySelection: true,
      propertyDetails: true,
    });

    toast.info("Đã xóa tất cả dữ liệu form", {
      position: "top-right",
      autoClose: 2000,
      icon: "🔄",
    });
  }, [
    handleResetForm,
    currentAnalysisState,
    closeDropdown,
    useFirebaseTracking,
    resetTracking,
  ]);

  // Handle analyze price
  const handleAnalyzePriceWrapper = useCallback(async () => {
    // Check form validation first
    if (!formValidation.isValid) {
      toast.error("Vui lòng điền đầy đủ thông tin bắt buộc!", {
        position: "top-right",
        autoClose: 3000,
        icon: "⚠️",
      });
      return;
    }

    console.log("🚀 Starting analyze price flow...");

    // Set submitting state to prevent spam clicks
    setIsSubmitting(true);

    try {
      // Call API to submit analysis request
      await currentAnalysisState.handleAnalyzePrice(formValidation, () => {
        console.log("✅ Analysis request submitted successfully");

        // Success callback - show success message and navigate immediately
        toast.success(
          <div className="flex flex-col sm:flex-row items-start sm:items-center">
            <FiClock className="mr-0 sm:mr-3 mb-2 sm:mb-0 text-green-500 text-xl sm:text-base flex-shrink-0" />
            <div className="text-left">
              <div className="font-semibold text-sm sm:text-base">
                Yêu cầu định giá đã được gửi!
              </div>
              <div className="text-xs sm:text-sm text-gray-600 mt-1">
                Đang chuyển đến trang theo dõi tiến độ...
              </div>
            </div>
          </div>,
          {
            position: "top-center",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            icon: false,
            className:
              "!bg-gradient-to-r from-green-50 to-emerald-50 !text-gray-800 !border !border-green-200 !mx-2 sm:!mx-4",
          }
        );

        // Navigate immediately to price history
        console.log("🧭 Navigating to price history immediately...");
        navigate("/price-history");
      });
    } catch (error) {
      console.error("❌ Error submitting analysis request:", error);

      // Check if error is due to daily limit reached
      if (error && error.reason === "limit_reached") {
        setLimitInfo({
          used: error.used || 0,
          limit: error.limit || USAGE_CONFIG.DEFAULT_DAILY_LIMIT,
        });
        setShowLimitModal(true);
        setIsSubmitting(false); // Reset submitting state for limit modal
        return;
      }

      toast.error("Có lỗi xảy ra khi gửi yêu cầu. Vui lòng thử lại!", {
        position: "top-right",
        autoClose: 3000,
        icon: "❌",
      });
    } finally {
      // Reset submitting state
      setIsSubmitting(false);
    }
  }, [currentAnalysisState, formValidation, navigate]);

  // Load saved data on component mount
  useEffect(() => {
    // Check authentication
    if (localStorage.getItem("userAccessToken") == null) {
      navigate("/");
      return;
    }

    // Load saved form data
    const savedFormData = loadSavedData();
    if (savedFormData?.propertyType) {
      setExpandedSections((prev) => ({
        ...prev,
        propertyDetails: true,
      }));
    }
  }, [navigate, loadSavedData]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupWebSocket();
      cleanupTestWebSocket();
    };
  }, [cleanupWebSocket, cleanupTestWebSocket]);

  // Determine if button should be disabled
  const isButtonDisabled = useMemo(() => {
    return (
      currentAnalysisState.isAnalyzing ||
      !formValidation.isValid ||
      isSubmitting
    );
  }, [currentAnalysisState.isAnalyzing, formValidation.isValid, isSubmitting]);

  // Get button text and styling
  const getButtonConfig = useMemo(() => {
    if (isSubmitting) {
      return {
        text: "Đang gửi yêu cầu...",
        className: "bg-gray-400 cursor-not-allowed",
      };
    }

    if (currentAnalysisState.isAnalyzing) {
      return {
        text: isTestMode ? "Đang gửi yêu cầu..." : "Đang gửi yêu cầu...",
        className: "bg-gray-400 cursor-not-allowed",
      };
    }

    if (!formValidation.isValid) {
      return {
        text: "Phân tích giá",
        className: "bg-gray-400 cursor-not-allowed",
      };
    }

    return {
      text: isTestMode ? "Test phân tích giá" : "Phân tích giá",
      className:
        "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 hover:shadow-xl active:scale-95 sm:hover:scale-105",
    };
  }, [
    isSubmitting,
    currentAnalysisState.isAnalyzing,
    formValidation.isValid,
    isTestMode,
  ]);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 pt-4 sm:pt-6 pb-6 sm:pb-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 text-center leading-tight">
            ĐỊNH GIÁ BẤT ĐỘNG SẢN AI
          </h1>
          <p className="text-center text-gray-600 mt-2 text-sm sm:text-base px-2">
            Nhập thông tin bất động sản để nhận được định giá chính xác từ AI
          </p>
        </div>

        {/* Quota Information */}
        <div className="w-full max-w-3xl mx-auto mb-4">
          <QuotaInfo />
        </div>

        {/* Single column layout - Form nhập liệu */}
        <div className="w-full max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
            {/* Chọn loại BĐS */}
            <PropertyTypeSelector
              selectedPropertyType={formData.propertyType}
              isOpen={activeDropdown === "propertyType"}
              onToggle={toggleDropdown}
              onChange={handlePropertyTypeChangeWrapper}
              dropdownRef={propertyTypeDropdownRef}
              isExpanded={expandedSections.propertySelection}
              onToggleSection={(e) => toggleSection("propertySelection", e)}
              transitionStyle={transitionStyle}
            />

            {/* Chi tiết BĐS */}
            <PropertyDetailsForm
              formData={formData}
              currentConfig={currentConfig}
              isExpanded={expandedSections.propertyDetails}
              onToggleSection={(e) => toggleSection("propertyDetails", e)}
              transitionStyle={transitionStyle}
              activeDropdown={activeDropdown}
              onDropdownToggle={toggleDropdown}
              onFormDataChange={handleFormDataChange}
              createRefCallback={createRefCallback}
              onCloseDropdown={closeDropdown}
            />

            {/* Nút actions */}
            {formData.propertyType && (
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mt-6 sm:mt-8 lg:mt-10">
                <button
                  type="button"
                  className="flex items-center justify-center py-3 sm:py-4 px-6 sm:px-8 rounded-lg font-medium text-sm sm:text-base text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm min-h-[48px] touch-manipulation"
                  onClick={handleResetFormWrapper}
                  disabled={currentAnalysisState.isAnalyzing}
                >
                  <FiRotateCcw className="mr-2 text-base sm:text-lg" />
                  Xóa form
                </button>

                <button
                  type="button"
                  className={`flex items-center justify-center py-3 sm:py-4 px-6 sm:px-8 rounded-lg font-semibold text-sm sm:text-base text-white transition-all duration-200 shadow-lg min-h-[48px] touch-manipulation ${getButtonConfig.className}`}
                  onClick={handleAnalyzePriceWrapper}
                  disabled={isButtonDisabled}
                >
                  {currentAnalysisState.isAnalyzing ? (
                    <>
                      <FiRefreshCw className="animate-spin mr-2 text-base sm:text-lg" />
                      <span className="hidden sm:inline">
                        {getButtonConfig.text}
                      </span>
                      <span className="sm:hidden">Đang gửi...</span>
                    </>
                  ) : (
                    <>
                      <FiDollarSign className="mr-2 text-base sm:text-lg" />
                      <span className="hidden sm:inline">
                        {getButtonConfig.text}
                      </span>
                      <span className="sm:hidden">Phân tích giá</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Loading state removed - user will be redirected to Price History Queue tab */}

            {/* Information section */}
            {formData.propertyType && (
              <div className="mt-6 sm:mt-8 p-4 sm:p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3 sm:gap-4">
                  <FiClock className="text-blue-500 flex-shrink-0 text-xl mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-800 mb-3 text-base sm:text-lg">
                      Quy trình định giá AI
                    </h3>
                    <ul className="text-xs sm:text-sm text-blue-700 space-y-2">
                      <li className="flex items-start">
                        <span className="mr-2 flex-shrink-0">•</span>
                        <span>
                          Hệ thống sẽ phân tích thông tin bất động sản của bạn
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 flex-shrink-0">•</span>
                        <span>Thời gian xử lý từ 15-30 phút</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 flex-shrink-0">•</span>
                        <span>Bạn sẽ nhận được thông báo khi có kết quả</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 flex-shrink-0">•</span>
                        <span>
                          Có thể theo dõi tiến độ trong mục "Lịch sử định giá"
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* NEW: Daily Usage Limit Modal */}
      {showLimitModal && (
        <UsageLimitModal
          onClose={() => setShowLimitModal(false)}
          usedCount={limitInfo.used}
          limit={limitInfo.limit}
        />
      )}

      <ToastContainer {...getOptimizedToastContainerProps()} />

      {/* Debug Tools (only in development) */}
      {process.env.NODE_ENV === "development" && (
        <React.Suspense fallback={null}></React.Suspense>
      )}
    </div>
  );
};

export default AIPrice;
